// These route keys mirror MODULE_CATALOG. The saved tenant role assignment is
// the permission source; this map only resolves a URL to its module key.
export const ROUTE_ACCESS = Object.fromEntries([
  "/dashboard", "/office", "/boardroom", "/analytics", "/accounts",
  "/journals", "/ledger", "/cashbook", "/reports", "/budgeting",
  "/reports-center", "/pos", "/services-pos", "/petty-cash",
  "/sales-history", "/invoicing", "/payables", "/purchase-orders",
  "/customers", "/suppliers", "/inventory", "/inventory-movements",
  "/productions", "/pharmacy", "/fixed-assets", "/bank-recon", "/payroll", "/audit-log", "/settings",
  "/hospital", "/school", "/microfinance",
].map((module) => [module, {}]));

// Public / always-allowed routes (auth pages, root redirects)
const PUBLIC_ROUTES = new Set(["/login", "/register", "/auth/callback", "/"]);

// Find the rule that best matches `pathname` — longest key that is a
// prefix of the current path. Returns `null` if no rule applies.
function resolveRule(pathname) {
  const keys = Object.keys(ROUTE_ACCESS).sort((a, b) => b.length - a.length);
  for (const k of keys) {
    if (pathname === k || pathname.startsWith(k + "/")) {
      return { key: k, rule: ROUTE_ACCESS[k] };
    }
  }
  return null;
}

const ROUTE_TABS = [
  ["/office/jobs", "/office", "jobs"],
  ["/office/people", "/office", "people"],
  ["/office/documents", "/office", "documents"],
  ["/office/mail", "/office", "mail"],
  ["/office/chat", "/office", "chat"],
  ["/office/calendar", "/office", "calendar"],
  ["/office/crm", "/office", "crm"],
  ["/office/memos", "/office", "memos"],
  ["/productions/dashboard", "/productions", "dashboard"],
  ["/productions/raw-materials", "/productions", "raw-materials"],
  ["/productions/finished-goods", "/productions", "finished-goods"],
  ["/productions/salespersons", "/productions", "salespersons"],
  ["/productions/customers", "/productions", "customers"],
  ["/productions/rm-receipts", "/productions", "rm-receipts"],
  ["/productions/orders", "/productions", "orders"],
  ["/productions/planning", "/productions", "planning"],
  ["/productions/sales", "/productions", "sales"],
  ["/productions/collections", "/productions", "collections"],
  ["/productions/commissions", "/productions", "commissions"],
  ["/productions/reports", "/productions", "reports"],
  ["/pharmacy/pos", "/pharmacy", "pos"],
  ["/pharmacy/products", "/pharmacy", "products"],
  ["/pharmacy/procurement", "/pharmacy", "procurement"],
  ["/pharmacy/receipts", "/pharmacy", "receipts"],
  ["/pharmacy/adjustments", "/pharmacy", "adjustments"],
  ["/pharmacy/movements", "/pharmacy", "movements"],
  ["/pharmacy/inventory", "/pharmacy", "inventory"],
  ["/hospital/pharmacy/inventory", "/hospital", "pharmacy:stock"],
  ["/hospital/pharmacy", "/hospital", "pharmacy"],
  ["/hospital/units", "/hospital", "clinical-units"],
  ["/hospital/triage", "/hospital", "triage"],
  ["/hospital/desk", "/hospital", "desk"],
  ["/hospital/diagnostics", "/hospital", "diagnostics"],
  ["/hospital/referrals", "/hospital", "referrals"],
  ["/hospital/follow-ups", "/hospital", "follow-ups"],
  ["/hospital/ward-care", "/hospital", "ward-care"],
  ["/pharmacy/desk", "/pharmacy", "desk"],
  ["/hospital/consultations", "/hospital", "consultations"],
  ["/hospital/settings", "/hospital", "settings"],
  ["/hospital/schedule", "/hospital", "schedule"],
  ["/hospital/services", "/hospital", "services"],
  ["/hospital/billing", "/hospital", "billing"],
  ["/hospital/records", "/hospital", "records"],
  ["/school/admissions", "/school", "admissions"],
  ["/school/staff", "/school", "staff"],
  ["/school/applications", "/school", "applications"],
  ["/school/website", "/school", "website"],
  ["/school/academics", "/school", "academics"],
  ["/school/attendance", "/school", "attendance"],
  ["/school/fees", "/school", "fees"],
  ["/school/courses", "/school", "courses"],
  ["/school/calendar", "/school", "calendar"],
  ["/school/notifications", "/school", "notifications"],
  ["/school/assessments", "/school", "assessments"],
  ["/school/timetable", "/school", "timetable"],
  ["/school/settings", "/school", "settings"],
  ["/microfinance/clients", "/microfinance", "clients"],
  ["/microfinance/teller", "/microfinance", "teller"],
  ["/microfinance/loans", "/microfinance", "loans"],
  ["/microfinance/import", "/microfinance", "import"],
  ["/microfinance/products", "/microfinance", "products"],
  ["/microfinance/interest", "/microfinance", "interest"],
  ["/microfinance/field-activity", "/microfinance", "field-activity"],
  ["/microfinance/field-ops", "/microfinance", "field-ops"],
  ["/microfinance/approvals", "/microfinance", "approvals"],
  ["/microfinance/audit", "/microfinance", "audit"],
  ["/microfinance/settings", "/microfinance", "settings"],
  ["/field", "/microfinance", "field"],
];

export function isTabAllowed(module, tab, role, grants) {
  if (role === "super_admin") return true;
  const tabs = Array.isArray(grants) ? undefined : grants?.tabs?.[module];
  return !Array.isArray(tabs) || tabs.includes(tab);
}

export function isMicrofinanceWorkspace(tenant) {
  const industry = tenant?.industry?.trim().toLocaleLowerCase() || "";
  const subSector = tenant?.sub_sector?.trim().toLocaleLowerCase() || "";
  return (industry.includes("financ") || industry.includes("bank")) &&
    ["microfinance", "savings & loans", "savings and loans", "credit union", "susu"].some((term) => subSector.includes(term));
}

function routeTab(pathname) {
  if (pathname === "/hospital") return ["/hospital", "/hospital", "home"];
  if (pathname === "/school") return ["/school", "/school", "home"];
  if (pathname === "/microfinance") return ["/microfinance", "/microfinance", "home"];
  return ROUTE_TABS.find(([prefix]) => pathname === prefix || pathname.startsWith(`${prefix}/`));
}

// Returns true if `role` may see `pathname`.
export function isRouteAllowed(pathname, role, grants = [], tenant = null) {
  if (!pathname) return true;
  if (role === "super_admin") return true;
  const mfiManagers = ["tenant_admin", "mfi_branch_manager"].includes(role);
  if (mfiManagers && isMicrofinanceWorkspace(tenant) && (
    pathname === "/field" || pathname.startsWith("/field/") ||
    pathname === "/microfinance" || pathname.startsWith("/microfinance/")
  )) return true;
  if (isMicrofinanceWorkspace(tenant) && role !== "tenant_admin" && pathname !== "/field" && !pathname.startsWith("/field/") &&
      pathname !== "/microfinance" && !pathname.startsWith("/microfinance/")) return false;
  const grantedModules = Array.isArray(grants) ? grants : grants?.modules || [];
  if (role === "sales_rep" && pathname.startsWith("/productions/")) {
    return pathname === "/productions/orders"
      && grantedModules.includes("/productions")
      && isTabAllowed("/productions", "orders", role, grants);
  }
  if (PUBLIC_ROUTES.has(pathname)) return true;
  const hit = resolveRule(pathname);
  const module = hit?.key || (pathname === "/field" || pathname.startsWith("/field/") ? "/microfinance" : null) || (pathname === "/banks" || pathname.startsWith("/banks/") ? "/bank-recon" : null) || (pathname.startsWith("/office/") ? "/office" : null);
  if (!module || !Array.isArray(grantedModules) || !grantedModules.includes(module)) return false;
  const moduleTabs = Array.isArray(grants) ? undefined : grants?.tabs?.[module];
  if (Array.isArray(moduleTabs) && moduleTabs.length === 0) return false;
  const tab = routeTab(pathname);
  if (tab && !isTabAllowed(tab[1], tab[2], role, grants)) {
    if (tab[2] !== "pharmacy") return false;
    const pharmacyTabs = grants?.tabs?.[tab[1]];
    return !Array.isArray(pharmacyTabs) || pharmacyTabs.some((key) => key.startsWith("pharmacy:"));
  }
  return true;
}

export function isAlwaysAvailableRoute(pathname) {
  return false;
}

// First route the current role IS allowed on — used to redirect away from
// a denied route so the user always lands somewhere useful.
export function firstAllowedRoute(role, grantedModules = [], tenant = null) {
  const effectiveModules = Array.isArray(grantedModules) ? grantedModules : grantedModules?.modules || [];
  const fallbacks = Array.isArray(tenant?.enabled_modules) ? tenant.enabled_modules : effectiveModules;

  if (role === "tenant_admin") {
    const preferred = ["/dashboard", "/reports", "/accounts", "/office/documents", "/microfinance", "/settings"];
    for (const p of preferred) {
      if (isRouteAllowed(p, role, fallbacks.length ? fallbacks : grantedModules, tenant)) return p;
    }
  }

  if (role !== "super_admin" && isMicrofinanceWorkspace(tenant)) {
    return role === "mfi_deposit_officer" ? "/field" : "/microfinance";
  }
  const roleLandingPaths = {
    sales_rep: "/pos",
    hr_clerk: "/payroll",
    viewer: "/reports-center",
    auditor: "/reports-center",
    admin_staff: "/office/documents",
    pharmacist: "/pharmacy",
    hospital_admin: "/hospital",
    doctor: "/hospital",
    nurse: "/hospital",
    midwife: "/hospital",
    lab_technician: "/hospital",
    radiographer: "/hospital",
    records_officer: "/hospital",
    claims_officer: "/hospital",
    procurement_officer: "/hospital",
    school_admin: "/school",
    teacher: "/school",
    admissions_officer: "/school",
    school_registrar: "/school",
    school_bursar: "/school",
    mfi_branch_manager: "/dashboard",
    mfi_teller: "/microfinance/teller",
    mfi_credit_officer: "/microfinance",
    mfi_deposit_officer: "/field",
  };
  const preferred = roleLandingPaths[role];
  if (preferred && isRouteAllowed(preferred, role, grantedModules, tenant)) return preferred;

  const priority = [
    "/pos",
    "/pharmacy/pos",
    "/pharmacy/inventory",
    "/pharmacy",
    "/hospital/triage",
    "/hospital/consultations",
    "/hospital/schedule",
    "/hospital/services",
    "/hospital/billing",
    "/hospital/records",
    "/hospital/pharmacy/inventory",
    "/hospital/pharmacy",
    "/hospital/units/diagnostics",
    "/hospital",
    "/school/admissions",
    "/school/assessments",
    "/school/timetable",
    "/school/settings",
    "/school",
    "/services-pos",
    "/payroll",
    "/reports-center",
    "/office/documents",
    "/dashboard",
    "/inventory",
    "/settings",
  ];
  for (const p of priority) {
    if (isRouteAllowed(p, role, grantedModules)) return p;
  }
  return "/";
}
